import { Inject, Injectable } from '@angular/core';
import { MatDialog, MatSnackBar } from '@angular/material';
import { environment } from 'src/environments/environment';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { CopyToEmrModalComponent } from 'src/app/copy-to-emr-modal/copy-to-emr-modal.component';
import { ClinicService } from 'src/app/core/api/clinic.service';
import { EncountersRoutes } from 'src/app/encounters/encounters.routes';
import { ClinicSetupModalComponent } from 'src/app/logged-in-navbar/clinic-setup-modal/clinic-setup-modal.component';
import { PatientImportWizardModalComponent } from 'src/app/patients/patient-import-wizard-modal/patient-import-wizard-modal.component';
import { PatientModalComponent } from 'src/app/shared/consult-forms/patient-modal/patient-modal.component';
import { NavigateToNewAssessmentModalComponent } from 'src/app/shared/navigate-to-new-assessment-modal/navigate-to-new-assessment-modal.component';
import { SelectLanguageModalComponent } from 'src/app/shared/select-language-modal/select-language-modal.component';
import { SendMultipleQuestionnairesDialogComponent } from 'src/app/shared/send-multiple-questionnaires-dialog/send-multiple-questionnaires-dialog.component';
import { SendQuestionnaireDialogComponent } from 'src/app/shared/send-questionnaire-dialog/send-questionnaire-dialog.component';
import { TabletQuestionnaireModalComponent } from 'src/app/shared/tablet-questionnaire-modal/tablet-questionnaire-modal.component';
import { DomainOption, HOSTING_DOMAIN_ACCESSOR } from '../domain-accessor';
import { TranslationsService } from './../../translations/translations.service';
import { PatientService } from './patient.service';
import { StaffService } from './staff.service';
import { AppStateService } from 'src/app/core/services/app-state.service';
import { PampIframeService } from 'src/app/core/services/pamp-iframe.service';

@Injectable({
  providedIn: 'root'
})
export class SideNavActionsService {
  constructor(
    private dialog: MatDialog,
    private staffService: StaffService,
    private patientService: PatientService,
    public router: Router,
    public route: ActivatedRoute,
    private clinicService: ClinicService,
    private translationsService: TranslationsService,
    private matSnackBar: MatSnackBar,
    private translocoService: TranslocoService,
    private appStateService: AppStateService,
    private pampIframeService: PampIframeService,
    @Inject(HOSTING_DOMAIN_ACCESSOR) private domain: DomainOption
  ) {}

  private translationSub: Subscription;

  public openCreatePatientModal() {
    this.staffService.getLoggedInStaff().subscribe(loggedInDoctor => {
      PatientModalComponent.open(this.dialog, loggedInDoctor);
    });
  }

  public openPatientImportWizard() {
    PatientImportWizardModalComponent.open(this.dialog, null);
  }

  ngOnDestroy() {
    if (this.translationSub) {
      this.translationSub.unsubscribe();
    }
  }

  openTabletQuestionnaire() {
    TabletQuestionnaireModalComponent.open(this.dialog, null, this.patientService.patient)
      .afterClosed()
      .subscribe(questionnaireRequestGenerated => {
        if (questionnaireRequestGenerated) {
          this.patientService.questionnaires = [
            questionnaireRequestGenerated,
            ...this.patientService.questionnaires
          ];
        }
      });
  }

  openSendQuestionnaire() {
    SendQuestionnaireDialogComponent.open(
      this.dialog,
      null,
      this.patientService.patient,
      {},
      null,
      true
    )
      .afterClosed()
      .subscribe(questionnaireRequestsSent => {
        if (questionnaireRequestsSent) {
          if (!Array.isArray(questionnaireRequestsSent)) {
            questionnaireRequestsSent = [questionnaireRequestsSent];
          }
          this.patientService.questionnaires = [
            ...questionnaireRequestsSent,
            ...this.patientService.questionnaires
          ];
        }
      });
  }

  public openSendMultipleQuestionnairesDialog() {
    SendMultipleQuestionnairesDialogComponent.open(this.dialog);
  }

  public openNavigateToNewAssessmentModal() {
    NavigateToNewAssessmentModalComponent.open(this.dialog);
  }

  routeToNewEncounter() {
    this.router.navigate([{ outlets: { encounters: EncountersRoutes.New } }], {
      relativeTo: this.route.parent
    });
  }

  public openCopyModal() {
    this.dialog.open(CopyToEmrModalComponent, {
      data: {
        showAssessmentList: true
      },
      width: '95vw',
      minWidth: '67rem',
      height: '92vh'
    });
  }

  public openClinicSetupModal({ selectedTabLabel, selectedSubTabLabel }: any) {
    this.clinicService.clinic$
      .pipe(take(1))
      .subscribe(clinic =>
        ClinicSetupModalComponent.open(
          this.dialog,
          clinic,
          this.domain !== DomainOption.Seema,
          {},
          selectedTabLabel || '',
          selectedSubTabLabel
        )
      );
  }

  public openSelectLanguageModal() {
    SelectLanguageModalComponent.open(this.dialog)
      .afterClosed()
      .pipe(take(1))
      .subscribe(currentSelectedLanguage => {
        if (!currentSelectedLanguage) {
          return;
        }
        const languageNameAndRegion = this.translationsService.languageCodeToFriendlyNameMap[
          currentSelectedLanguage
        ];
        const message =
          languageNameAndRegion.name +
          (languageNameAndRegion.region ? ` (${languageNameAndRegion.region})` : '');

        let dismissText = 'Dismiss';
        this.translationSub = this.translocoService.selectTranslate('Dismiss').subscribe(text => {
          dismissText = text || dismissText;
        });

        this.matSnackBar.open(message, dismissText, {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom'
        });
      });
  }

  public launchPampApp() {
    this.pampIframeService.openPampApp();
  }

  public launchProductRefillRequests() {
    this.pampIframeService.openProductRefillRequests();
  }
}
